import React, { useState, useEffect } from "react";
import Select from "react-select";
import "./index.css";
// 1. Import real Firebase Auth functions and configuration helpers
import { auth, signInWithGoogle, signOutUser } from "./firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";


const COST_PER_KM_INR = 8;
// 🛑 CRITICAL FIX: Retain the EC2 Public IP and Port 5000
const EC2_API_BASE_URL = "http://65.0.135.255:5000"; 
// Note: Use your actual Public IPv4 address here.

const Navbar = ({ selectedCurrency, setSelectedCurrency, exchangeRates, isLoading }) => {
    const getFlagEmoji = (currencyCode) => {
        const exceptions = {
            EUR: "EU",
            USD: "US",
            GBP: "GB",
            XOF: "BF",
            XAF: "CM",
            XCD: "AG",
            AUD: "AU",
            CAD: "CA",
            SGD: "SG",
            CHF: "CH",
            CNY: "CN",
            INR: "IN",
            JPY: "JP",
            KRW: "KR",
            MYR: "MY"
        };
        const countryCode = exceptions[currencyCode] || currencyCode.slice(0, 2);
        return String.fromCodePoint(...[...countryCode.toUpperCase()].map(c => 0x1F1E6 - 65 + c.charCodeAt()));
    };

    const options = Object.keys(exchangeRates).map((code) => ({
        value: code,
        label: `${getFlagEmoji(code)} ${code}`,
    }));

    return (
        <nav className="navbar">
            <div className="menu">
                <div className="currency-header">Choose Currency</div>
                {!isLoading && options.length > 0 ? (
                    <Select
                        className="currency-dropdown"
                        options={options}
                        value={{
                            value: selectedCurrency,
                            label: `${getFlagEmoji(selectedCurrency)} ${selectedCurrency}`,
                        }}
                        onChange={(selected) => setSelectedCurrency(selected.value)}
                        isSearchable
                        styles={{
                            control: (baseStyles) => ({
                                ...baseStyles,
                                backgroundColor: "rgba(0, 0, 0, 0.32)",
                                color: "white",
                                border: "none",
                                boxShadow: "none",
                            }),
                            singleValue: (baseStyles) => ({
                                ...baseStyles,
                                color: "white",
                            }),
                            menu: (baseStyles) => ({
                                ...baseStyles,
                                backgroundColor: "rgba(0, 0, 0, 0.32)",
                            }),
                            option: (baseStyles, state) => ({
                                ...baseStyles,
                                backgroundColor: state.isFocused ? "rgba(255, 255, 255, 0.1)" : "transparent",
                                color: "white",
                            }),
                            input: (baseStyles) => ({
                                ...baseStyles,
                                color: "white",
                            }),
                        }}
                    />
                ) : (
                    <div className="loading-message">Loading currencies...</div>
                )}
            </div>
        </nav>
    );
};

const cityData = {
    "Paris, France": [48.8566, 2.3522],
    "Rome, Italy": [41.9028, 12.4964],
    "Barcelona, Spain": [41.3851, 2.1734],
    "Madrid, Spain": [40.4168, -3.7038],
    "London, UK": [51.5074, -0.1278],
    "Berlin, Germany": [52.52, 13.405],
    "Amsterdam, Netherlands": [52.3676, 4.9041],
    "Athens, Greece": [37.9838, 23.7275],
    "Istanbul, Turkey": [41.0082, 28.9784],
    "Antalya, Turkey": [36.8969, 30.7133],
    "Budapest, Hungary": [47.4979, 19.0402],
    "Tokyo, Japan": [35.6895, 139.6917],
    "Mumbai, India": [19.076, 72.8777],
    "Bangkok, Thailand": [13.7563, 100.5018],
    "Singapore, Singapore": [1.3521, 103.8198],
    "Bali, Indonesia": [-8.4095, 115.1889],
    "Dubai, UAE": [25.276987, 55.296249],
    "Rio de Janeiro, Brazil": [-22.9068, -43.1729],
    "Marrakech, Morocco": [31.6295, -7.9811],
    "Sydney, Australia": [-33.8688, 151.2093],
    "Melbourne, Australia": [-37.8136, 144.9631],
    "Kuala Lumpur, Malaysia": [3.139, 101.6869],
    "Cairo, Egypt": [30.0444, 31.2357],
};

const getDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const App = () => {
    const [fromInput, setFromInput] = useState("");
    const [toInput, setToInput] = useState("");
    const [budgetInput, setBudgetInput] = useState("");
    const [selectedCurrency, setSelectedCurrency] = useState("INR");
    const [result, setResult] = useState("");
    const [alertMessage, setAlertMessage] = useState("");
    const [fromSuggestions, setFromSuggestions] = useState([]);
    const [toSuggestions, setToSuggestions] = useState([]);
    const [exchangeRates, setExchangeRates] = useState({ INR: 1 });
    const [isLoading, setIsLoading] = useState(true);
    
    // 🎯 State to hold the authenticated Firebase user object
    const [userProfile, setUserProfile] = useState(null); 
    const isUserLoggedIn = !!userProfile;

    // 2. Re-implement the Firebase observer to update state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                // Firebase user object contains email, displayName, uid, etc.
                setUserProfile(user); 
                setAlertMessage(`Logged in as ${user.displayName}.`);
                
                // 🛑 REMOVED: No longer calling handleCreateProfile() here.
                // The logic is now inside handleSaveTrip().
            } else {
                setUserProfile(null);
                setAlertMessage("Please log in to save your trips.");
            }
        });

        return () => unsubscribe(); // Cleanup
    }, []); 

    // ... (other useEffect hooks remain unchanged) ...
    useEffect(() => {
        fetch("https://open.er-api.com/v6/latest/INR")
            .then((res) => res.json())
            .then((data) => {
                setExchangeRates(data.rates || { INR: 1 });
                setIsLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching rates:", err);
                setExchangeRates({ INR: 1 });
                setIsLoading(false);
            });
    }, []);
    
    useEffect(() => {
        if (fromInput === "" || toInput === "" || budgetInput === "") {
            setResult("");
            setAlertMessage("");
        }
    }, [fromInput, toInput, budgetInput]);
    

    const updateFromSuggestions = (value) => {
        setFromInput(value);
        setFromSuggestions(
            value
                ? Object.keys(cityData).filter((city) =>
                      city.toLowerCase().startsWith(value.toLowerCase())
                  )
                : []
        );
    };

    const updateToSuggestions = (value) => {
        setToInput(value);
        setToSuggestions(
            value
                ? Object.keys(cityData).filter((city) =>
                      city.toLowerCase().startsWith(value.toLowerCase())
                  )
                : []
        );
    };
    
    // 3. Simple Login Handler (uses signInWithPopup from firebaseConfig.js)
    const handleGoogleLogin = async () => {
        try {
            await signInWithGoogle();
        } catch (error) {
            setAlertMessage(`Login Failed: ${error.message}`);
        }
    };


    // 🚀 UPDATED SAVE TRIP FUNCTION: Targets EC2 IP and sends Profile Data
    const handleSaveTrip = async (calculatedCost, travelDate) => {
        if (!isUserLoggedIn) {
            setAlertMessage("⚠️ Please log in to save a trip.");
            return;
        }
        if (!fromInput || !toInput || !calculatedCost) {
            setAlertMessage("Please calculate the cost first.");
            return;
        }

        // 🎯 Target the running EC2 server's /storeTrip endpoint
        const apiUrl = `${EC2_API_BASE_URL}/storeTrip`; 
        
        const tripData = {
            // CRITICAL: All fields needed by the Node.js server
            email: userProfile.email,         // Partition Key for UserTrips
            name: userProfile.displayName,    // For user_profiles table
            uid: userProfile.uid,             // PK for user_profiles table
            
            from: fromInput,
            to: toInput,
            date: travelDate,
            budget: calculatedCost, 
        };

        try {
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(tripData),
            });

            if (response.ok) {
                const data = await response.json();
                setAlertMessage(`✅ Trip saved successfully! Profile and trip data updated. Server: ${data.message}`);
            } else {
                const errorData = await response.json().catch(() => ({error: "Unknown response error"}));
                setAlertMessage(`❌ Failed to save trip. Check EC2 console logs: ${errorData.error}`);
            }
        } catch (error) {
            console.error("Fetch error:", error);
            setAlertMessage("🛑 Network error: Check EC2 Security Group (Port 5000 is open).");
        }
    };
    // --------------------------------------------------------------------


    const handleCostCalculation = () => {
        setAlertMessage("");
        if (!fromInput || !toInput) {
            setAlertMessage("Please enter both 'From' and 'To' destinations.");
            setResult("");
            return;
        }

        const fromCoords = cityData[fromInput];
        const toCoords = cityData[toInput];

        if (!fromCoords || !toCoords) {
            setAlertMessage("Invalid 'From' or 'To' destination. Please select from the list.");
            setResult("");
            return;
        }

        const distance = getDistance(fromCoords[0], fromCoords[1], toCoords[0], toCoords[1]);
        const travelCostINR = Math.round(distance * COST_PER_KM_INR);
        const travelCost = Math.round(travelCostINR * (exchangeRates[selectedCurrency] || 1));
        
        const currentDate = new Date().toISOString().split('T')[0];
        
        setResult(
            <div className="result-box">
                <h2>Travel Cost Details</h2>
                <p><strong>From:</strong> {fromInput}</p>
                <p><strong>To:</strong> {toInput}</p>
                <p><strong>Estimated Cost:</strong> {selectedCurrency} {travelCost}</p>
                {/* Button: Calls the handleSaveTrip */}
                <button 
                    className="save-button" 
                    onClick={() => handleSaveTrip(travelCost, currentDate)}
                    disabled={!isUserLoggedIn} 
                >
                    💾 Save Trip to AWS (EC2/DynamoDB)
                </button>
                {!isUserLoggedIn && <p className="login-warning">Log in to enable saving.</p>}
            </div>
        );
    };

    const handleBudgetDestinations = () => {
        setAlertMessage("");
        if (!fromInput || !budgetInput) {
            setAlertMessage("Please enter a valid 'From' destination and your budget.");
            setResult("");
            return;
        }

        const fromCoords = cityData[fromInput];
        if (!fromCoords) {
            setAlertMessage("Invalid 'From' destination. Please select from the list.");
            setResult("");
            return;
        }

        const budgetINR = budgetInput / (exchangeRates[selectedCurrency] || 1);
        let possibleDestinations = [];

        Object.keys(cityData).forEach((destination) => {
            if (destination !== fromInput) {
                const toCoords = cityData[destination];
                const distance = getDistance(fromCoords[0], fromCoords[1], toCoords[0], toCoords[1]);
                const travelCostINR = Math.round(distance * COST_PER_KM_INR);

                if (travelCostINR <= budgetINR) {
                    possibleDestinations.push(destination);
                }
            }
        });

        if (possibleDestinations.length === 0) {
            setResult("No destinations found within your budget.");
        } else {
            setResult(
                <div className="result-box">
                    <h2>Possible Destinations</h2>
                    <ul className="destination-list">
                        {possibleDestinations.map((dest) => (
                            <li key={dest} className="destination-item">{dest}</li>
                        ))}
                    </ul>
                </div>
            );
        }
    };

    return (
        <div className="app-container">
            <a href='https://www.sideprojectors.com/project/91585/map-my-trip' target="_blank" rel="noopener noreferrer">
            <img 
                style={{position:'fixed', zIndex:1000, top:'-5px', right:'20px', border:0}} 
                src='https://www.sideprojectors.com/img/badges/badge_2_red.png' 
                alt='Map My Trip is for sale at @SideProjectors' 
            />
            </a>
            <Navbar
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
                exchangeRates={exchangeRates}
                isLoading={isLoading}
            />

            <div className="main-box">
                <p className="creator-portfolio">
                    Creator portfolio : <a href="https://www.linkedin.com/in/madhuvanthi-b-762a92287/" target="_blank" rel="noopener noreferrer">link</a>
                </p>
                <h1>🛫 MapMyTrip ({selectedCurrency}) 🌎</h1>
                
                {/* AUTH BUTTON: Toggles between Login and Logout using real Firebase state */}
                {userProfile ? (
                    <>
                        <button 
                            className="login-button" 
                            onClick={signOutUser}
                        >
                            Log Out ({userProfile.displayName || 'User'})
                        </button>
                    </>
                ) : (
                    <button 
                        className="login-button" 
                        onClick={handleGoogleLogin}
                    >
                        Log In with Google
                    </button>
                )}
                
                {/* The alert message shows status */}
                {alertMessage && <div className="alert-box">{alertMessage}</div>}

                <div className="input-container">
                    <input
                        type="text"
                        placeholder="From Destination"
                        value={fromInput}
                        onChange={(e) => updateFromSuggestions(e.target.value)}
                    />
                    {fromSuggestions.length > 0 && (
                        <ul className="suggestions-list">
                            {fromSuggestions.map((city) => (
                                <li key={city} onClick={() => {
                                    setFromInput(city);
                                    setFromSuggestions([]);
                                }}>{city}</li>
                            ))}
                        </ul>
                    )}
                    <input
                        type="text"
                        placeholder="To Destination"
                        value={toInput}
                        onChange={(e) => updateToSuggestions(e.target.value)}
                    />
                    {toSuggestions.length > 0 && (
                        <ul className="suggestions-list">
                            {toSuggestions.map((city) => (
                                <li key={city} onClick={() => {
                                    setToInput(city);
                                    setToSuggestions([]);
                                }}>{city}</li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="button-container">
                    <button onClick={handleCostCalculation}>Calculate Cost</button>
                    <hr className="white-line" />
                </div>

                <div className="input-container">
                    <input
                        type="number"
                        placeholder={`Enter your budget for one-way flight (${selectedCurrency})`}
                        value={budgetInput}
                        onChange={(e) => setBudgetInput(e.target.value)}
                    />
                </div>

                <div className="button-container">
                    <button onClick={handleBudgetDestinations}>Find Destinations</button>
                </div>

                <div className="result-container">{result}</div>
            </div>
        </div>
    );
};

export default App;
